// "Tem horário?": a cliente pergunta no WhatsApp e a dona responde em segundos, com a voz dela.
// Escolhe a cliente → o serviço de sempre já vem → 3 vagas que encostam na agenda → texto pronto para colar.
// Nada é marcado aqui: quando ela topar, "Ela topou" abre o agendamento já preenchido.
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api } from '../api.ts';
import { useNav } from '../app/nav.tsx';
import { useToast } from '../app/toast.tsx';
import { useClients, useMe, useServices, type ClientRow } from '../lib/data.ts';
import { agoLabel, ddmm, DOW_LONG, dowIndex, dur, first, money, nowLocal } from '../lib/format.ts';
import { Avatar } from '../ui/bits.tsx';
import { Icon } from '../ui/Icon.tsx';
import { Sheet } from '../ui/Sheet.tsx';
import { Failed, Skeleton } from '../ui/State.tsx';

type Opt = { date: string; time: string; professionalId: string; professional: string; hugs: 'antes' | 'depois' | null };
const FILTERS = [['best', 'Melhores'], ['evening', 'Depois das 18h'], ['saturday', 'Sábado']] as const;

export function TemHorarioSheet({ clientId: initial }: { clientId?: string }) {
  const nav = useNav();
  const toast = useToast();
  const me = useMe().data!;
  const settings = me.salon.settings;
  const services = (useServices().data ?? []).filter((s) => s.active);
  const [query, setQuery] = useState('');
  const clients = useClients(query, 'all');
  const [client, setClient] = useState<ClientRow | null>(null);
  const [svcId, setSvcId] = useState<string | null>(null);
  const [pickSvc, setPickSvc] = useState(false);
  const [filter, setFilter] = useState<(typeof FILTERS)[number][0]>('best');
  const [sel, setSel] = useState<number | null>(null);
  const today = nowLocal().date;

  // cliente que veio da ficha
  const fromFile = initial && !client ? (clients.data ?? []).find((c) => c.id === initial) : undefined;
  const who = client ?? fromFile ?? null;
  // o serviço de sempre (o último que ela fez), trocável com um toque
  const usual = useMemo(() => services.find((s) => s.name === who?.last_service) ?? null, [services, who?.last_service]);
  const svc = services.find((s) => s.id === svcId) ?? usual ?? null;

  const best = useQuery({
    queryKey: ['bestSlots', svc?.id, filter],
    queryFn: () => api.get<{ options: Opt[] }>(`/availability/best?serviceId=${svc!.id}&filter=${filter}${filter === 'saturday' ? '&days=30' : ''}`),
    enabled: !!svc,
  });
  const opts = best.data?.options ?? [];
  const pick = sel != null ? opts[sel] : undefined;

  const dayTxt = (d: string) => (d === today ? 'hoje' : `${DOW_LONG[dowIndex(d)]!.toLowerCase()}, ${ddmm(d)}`);
  const rule = settings.deposit.mode === 'never' || !settings.pixKey.trim()
    ? 'Não pedimos sinal; se não puder vir, é só avisar.'
    : `Para garantir, pedimos um sinal ${settings.deposit.kind === 'fixed' ? `de ${money(settings.deposit.fixedCents)}` : `de ${settings.deposit.bps / 100}%`} no Pix.`;
  const text = pick && svc && who
    ? `Oi, ${first(who.name)}! Tenho ${dayTxt(pick.date)} às ${pick.time.replace(':00', 'h')}${pick.professionalId !== me.professionalId ? ` com a ${first(pick.professional)}` : ''} para ${svc.name.toLowerCase()} (${dur(svc.durationMin)}, ${money(svc.priceCents)}). Posso reservar? ${rule}`
    : '';

  if (!who) {
    return (
      <Sheet title="Tem horário?" sub="Para quem?">
        <div className="search-in"><Icon name="search" /><input autoFocus type="search" placeholder="Nome ou telefone" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Buscar cliente" /></div>
        {clients.data === undefined ? (clients.isError ? <Failed compact error={clients.error} retry={() => clients.refetch()} /> : <Skeleton kind="sheet" rows={5} />) : (
          <div className="cl-rows">
            {clients.data.slice(0, 60).map((c) => (
              <button key={c.id} className="cl-row" onClick={() => { setClient(c); setSel(null); }}>
                <Avatar id={c.id} name={c.name} />
                <span><strong>{c.name}</strong><small>{c.last_visit ? `${agoLabel(c.days_away ?? 0)} · ${c.last_service ?? ''}` : 'primeira vez'}</small></span>
                <Icon name="chev" />
              </button>
            ))}
            {!clients.data.length && <p className="m-hint">Ninguém com esse nome. Para cliente nova, use "Novo agendamento".</p>}
          </div>
        )}
      </Sheet>
    );
  }

  return (
    <Sheet title={`Tem horário para a ${first(who.name)}?`} onBack={initial ? undefined : () => { setClient(null); setSel(null); }}
      actions={pick ? <>
        <button className="cta" onClick={() => navigator.clipboard?.writeText(text).then(() => toast('Copiado. Cole na conversa dela.')).catch(() => toast('Não deu para copiar. Selecione o texto e copie.', 'warn'))}>Copiar para o WhatsApp</button>
        <button className="cta ghost" onClick={() => nav.open({ kind: 'novo', clientId: who.id, date: pick.date, time: pick.time, professionalId: pick.professionalId })}>Ela topou: agendar</button>
      </> : undefined}>
      {svc && !pickSvc ? (
        <button className="th-svc" onClick={() => setPickSvc(true)}><span><b>{svc.name}</b> · {dur(svc.durationMin)} · {money(svc.priceCents)}</span><em>trocar</em></button>
      ) : (
        <div className="chipset th-svcs" aria-label="Serviço">{services.map((s) => <button key={s.id} className={svc?.id === s.id ? 'on' : ''} onClick={() => { setSvcId(s.id); setPickSvc(false); setSel(null); }}>{s.name}</button>)}</div>
      )}
      {svc && <>
        <div className="pill-row th-filters" role="group" aria-label="Filtro">{FILTERS.map(([k, l]) => <button key={k} aria-pressed={filter === k} onClick={() => { setFilter(k); setSel(null); }}>{l}</button>)}</div>
        {best.data === undefined ? (best.isError ? <Failed compact error={best.error} retry={() => best.refetch()} /> : <Skeleton kind="sheet" rows={3} />) : !opts.length ? (
          <p className="m-hint">Sem vaga {filter === 'evening' ? 'depois das 18h' : filter === 'saturday' ? 'aos sábados' : ''} nas próximas semanas. Veja todos os horários na agenda.</p>
        ) : (
          <div className="th-slots" role="radiogroup" aria-label="Vagas">
            {opts.map((o, i) => (
              <button key={o.date + o.time} role="radio" aria-checked={sel === i} className="th-slot" style={{ animationDelay: `${i * 40}ms` }} onClick={() => setSel(i)}>
                <b>{dayTxt(o.date)} · {o.time}</b>
                <span>{o.professionalId === me.professionalId ? 'com você' : `com ${first(o.professional)}`}{o.hugs ? ` · ${o.hugs === 'antes' ? 'logo depois de outro horário' : 'logo antes de outro horário'}` : ''}</span>
              </button>
            ))}
          </div>
        )}
        <button className="link-btn th-all" onClick={() => nav.open({ kind: 'novo', clientId: who.id })}>Ver todos os horários</button>
        {text && <div className="th-msg" aria-label="Mensagem pronta">{text}</div>}
      </>}
    </Sheet>
  );
}
